import {
  resolveBrokerageFirmEmail,
  resolveBrokerageTeamMemberEmail,
  resolveInstallationBrokerageTeamMemberEmail,
} from "./brokerageTeamMemberEmail";
import { safeConsole } from "utils/envUtils";
import { getAuthCookie } from "./authCookies";

export type InvoicePublicKind = "sales" | "installation" | "removal";

export const INVOICE_EMAIL_TRIGGER = "Invoice_for_order";
export const BATCH_INVOICE_EMAIL_TRIGGER = "Invoice_for_Batch_Payment";

/**
 * Path-only segment for `GET ${REACT_APP_API_URL}/sales-orders/{id}/invoice-pdf` (Bearer).
 * Used when attaching the server invoice PDF to {@link INVOICE_EMAIL_TRIGGER} emails.
 */
export function getSalesOrderInvoicePdfPath(salesOrderId: string): string {
  const id = String(salesOrderId || "").trim();
  if (!id) return "";
  return `/sales-orders/${encodeURIComponent(id)}/invoice-pdf`;
}

/**
 * Path-only segment for `GET ${REACT_APP_API_URL}/installation-orders/{id}/invoice-pdf` (Bearer).
 */
export function getInstallationOrderInvoicePdfPath(installationOrderId: string): string {
  const id = String(installationOrderId || "").trim();
  if (!id) return "";
  return `/installation-orders/${encodeURIComponent(id)}/invoice-pdf`;
}

/**
 * Path-only segment for `GET ${REACT_APP_API_URL}/removal-orders/{id}/invoice-pdf` (Bearer).
 */
export function getRemovalOrderInvoicePdfPath(removalOrderId: string): string {
  const id = String(removalOrderId || "").trim();
  if (!id) return "";
  return `/removal-orders/${encodeURIComponent(id)}/invoice-pdf`;
}

/** Path-only segment for `GET ${REACT_APP_API_URL}/batch-payments/reports/{id}/invoice-pdf` (Bearer). */
export function getBatchPaymentReportInvoicePdfPath(reportId: string): string {
  const id = String(reportId || "").trim();
  if (!id) return "";
  return `/batch-payments/reports/${encodeURIComponent(id)}/invoice-pdf`;
}

/** Same date parsing as InvoiceDocument (ISO, Mongo `$date`, etc.). */
export function parseOrderDateValue(v: unknown): Date | null {
  if (v == null || v === "") return null;
  if (v instanceof Date && !Number.isNaN(v.getTime())) return v;
  if (typeof v === "object" && v !== null && "$date" in (v as Record<string, unknown>)) {
    return parseOrderDateValue((v as { $date?: unknown }).$date);
  }
  if (typeof v === "string" || typeof v === "number") {
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  return null;
}

export function formatDDMMYYYY(d: Date | null): string {
  if (!d || Number.isNaN(d.getTime())) return "-";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
}

/**
 * Params for {@link INVOICE_EMAIL_TRIGGER} / `emailConfig` "Invoice_for_order".
 */
export function buildInvoiceEmailParams(order: any): Record<string, string> {
  const orderNum = String(order?.order_number ?? order?.orderNumber ?? "").trim();
  const jobNum = String(order?.job_number ?? order?.jobNumber ?? "").trim();
  const invNum = String(order?.invoice_number ?? order?.invoiceNumber ?? "").trim();

  const orderIdForSubject = orderNum || jobNum || String(order?.id ?? order?._id ?? "").trim() || "—";

  const invDate = parseOrderDateValue(order?.invoice_date ?? order?.invoiceDate);
  const paymentInfoDueDate = Array.isArray(order?.payment_info)
    ? order.payment_info.find((p: any) => String(p?.due_date ?? "").trim())?.due_date
    : undefined;
  const dueRaw =
    paymentInfoDueDate ??
    order?.payment_due_date ??
    order?.due_date ??
    order?.dueDate ??
    order?.paymentDueDate;
  const dueDate = parseOrderDateValue(dueRaw);

  const total = Number(order?.total_amount ?? 0);
  const totalPaid = (order?.payment_info ?? []).reduce(
    (acc: number, p: { amount_paid?: number | string }) => acc + Number(p?.amount_paid || 0),
    0
  );
  const remainingAmount = total - totalPaid;
  const amountDue = `$${remainingAmount.toFixed(2)}`;

  const vendorName = String(
    order?.signage_company_name ??
      order?.signageCompanyName ??
      order?.vendor_name ??
      ""
  ).trim() || "—";

  const firstName = String(
    order?.customer_first_name ??
      order?.team_member_name?.split?.(/\s+/)?.[0] ??
      order?.customer_email?.split?.("@")?.[0] ??
      "there"
  ).trim() || "there";

  return {
    firstName,
    orderId: orderIdForSubject,
    invoiceNumber: orderIdForSubject || invNum || "—",
    invoiceDate: formatDDMMYYYY(invDate),
    amountDue,
    dueDate: formatDDMMYYYY(dueDate),
    vendorName,
  };
}

const INVOICE_PUBLIC_PATH: Record<InvoicePublicKind, string> = {
  sales: "/salesorder/invoice",
  installation: "/installationorder/invoice",
  removal: "/removaljob/invoice",
};

/** Public invoice URL with Stripe query params — same pattern as order-summary `viewPayUrl`. */
export async function buildInvoicePublicViewPayUrl(
  order: any,
  token: string,
  kind: InvoicePublicKind
): Promise<string> {
  const baseUrl = (
    process.env.REACT_APP_BASE_URL ||
    (typeof window !== "undefined" ? window.location.origin : "") ||
    ""
  ).replace(/\/+$/, "");
  const orderIdForPay = String(order?._id || order?.id || "").trim();
  let projectIdFromQuery = String(order?.project_id || "").trim();
  let accessProjectIdForPay = projectIdFromQuery;
  let accessTokenForPay = "";
  const payUserIdForStripeLinks = String(getAuthCookie("userId") || order?.signage_id || "").trim();

  if (token && projectIdFromQuery) {
    try {
      const { fetchProjectPublicAccess } = await import("components/proejctsandquoteapicalls");
      const accessRes = await fetchProjectPublicAccess(token, projectIdFromQuery);
      const accessData: any = (accessRes as any)?.data || {};
      const rawAccess: any = accessRes as any;
      accessProjectIdForPay = String(
        accessData.project_id ||
          accessData.projectId ||
          rawAccess?.project_id ||
          projectIdFromQuery ||
          ""
      ).trim();
      accessTokenForPay = String(
        accessData.access_token ||
          accessData.accessToken ||
          rawAccess?.access_token ||
          rawAccess?.accessToken ||
          ""
      ).trim();
      const publicUrlValue = String(
        accessData.public_url || accessData.publicUrl || rawAccess?.public_url || ""
      ).trim();
      if (!accessTokenForPay && publicUrlValue) {
        try {
          const parsed = publicUrlValue.startsWith("http")
            ? new URL(publicUrlValue)
            : new URL(publicUrlValue, window.location.origin);
          accessTokenForPay = String(
            parsed.searchParams.get("access_token") || parsed.searchParams.get("token") || ""
          ).trim();
        } catch {
          /* ignore */
        }
      }
    } catch (e) {
      safeConsole.warn("fetchProjectPublicAccess (invoice email):", e);
    }
  }

  let viewPayUrl = `${baseUrl}${INVOICE_PUBLIC_PATH[kind]}?project_id=${encodeURIComponent(accessProjectIdForPay)}&order_id=${encodeURIComponent(orderIdForPay)}&access_token=${encodeURIComponent(accessTokenForPay)}${payUserIdForStripeLinks ? `&supplier_id=${encodeURIComponent(payUserIdForStripeLinks)}` : ""}`;

  try {
    const { createStripePaymentIntent, fetchPaymentGatewaysCreatedByUserId } = await import(
      "components/proejctsandquoteapicalls"
    );
    const orderTotal = Number(order?.total_amount || 0);
    const paidSoFar = (Array.isArray(order?.payment_info) ? order.payment_info : []).reduce(
      (acc: number, p: { amount_paid?: number | string }) => acc + Number(p?.amount_paid || 0),
      0
    );
    const remainingToCharge = Math.max(0, Math.round((orderTotal - paidSoFar) * 100) / 100);
    const amountCents = Math.max(0, Math.round(remainingToCharge * 100));
    let stripeClientSecret = "";
    if (token && payUserIdForStripeLinks && amountCents > 0) {
      const pi = await createStripePaymentIntent(token, payUserIdForStripeLinks, {
        amount: amountCents,
        currency: "usd",
      });
      stripeClientSecret = String((pi as any)?.client_secret || (pi as any)?.clientSecret || "").trim();
    }
    let stripePublishableKeyForUrl = "";
    if (token && payUserIdForStripeLinks) {
      const gateways = await fetchPaymentGatewaysCreatedByUserId(token, payUserIdForStripeLinks);
      const stripeGw = gateways.find((g: any) => String(g?.gateway || "").toLowerCase() === "stripe");
      stripePublishableKeyForUrl = String(stripeGw?.publishable_key || "").trim();
    }
    const withClientSecret = (u: string) => {
      if (!stripeClientSecret || !u) return u;
      const sep = u.includes("?") ? "&" : "?";
      return `${u}${sep}client_secret=${encodeURIComponent(stripeClientSecret)}`;
    };
    const withPublishableKey = (u: string) => {
      if (!stripePublishableKeyForUrl || !u) return u;
      const sep = u.includes("?") ? "&" : "?";
      return `${u}${sep}publishable_key=${encodeURIComponent(stripePublishableKeyForUrl)}`;
    };
    viewPayUrl = withPublishableKey(withClientSecret(viewPayUrl));
  } catch (e) {
    safeConsole.warn("Stripe helpers (invoice email):", e);
  }

  return viewPayUrl;
}

/** Invoice email trigger params including `viewPayUrl` for the public invoice Pay page. */
export async function buildInvoiceEmailParamsForSend(
  order: any,
  token: string,
  kind: InvoicePublicKind
): Promise<Record<string, string>> {
  const base = buildInvoiceEmailParams(order);
  let viewPayUrl = "";
  try {
    viewPayUrl = await buildInvoicePublicViewPayUrl(order, token, kind);
  } catch (e) {
    safeConsole.warn("buildInvoicePublicViewPayUrl:", e);
  }
  return { ...base, viewPayUrl };
}

/** Decode a base64 PDF string (from `sendTriggerEmails`-style attachments) into a `Blob`. */
export function base64PdfToBlob(base64: string): Blob {
  const s = String(base64 || "").trim();
  const bin = atob(s);
  const len = bin.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) bytes[i] = bin.charCodeAt(i);
  return new Blob([bytes], { type: "application/pdf" });
}

/** Trigger a file download in the browser for an in-memory blob. */
export function triggerBrowserDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1500);
}

export function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onloadend = () => {
      const s = String(r.result ?? "");
      const i = s.indexOf(",");
      resolve(i >= 0 ? s.slice(i + 1) : s);
    };
    r.onerror = () => reject(new Error("Failed to read PDF blob"));
    r.readAsDataURL(blob);
  });
}

export type InvoiceRecipientBundle = { licenseTypes: string[]; emails: string[] };

export type InvoiceSendRecipientPlan = {
  /** Brokerage / customer recipients (existing invoice flow). */
  customerRecipients: InvoiceRecipientBundle;
  /** Accounts & Management role assignees — sent separately as SIGNAGE_FIRM. */
  accountEmails: string[];
};

/** Signage role whose assigned users also receive invoice emails. */
export const INVOICE_ACCOUNTS_MANAGEMENT_ROLE = "Accounts & Management";

function firstNonEmpty(...vals: unknown[]): string {
  for (const v of vals) {
    const s = String(v ?? "").trim();
    if (s) return s;
  }
  return "";
}

/**
 * Recipients: brokerage firm (admin), team member, agent (customer email) — deduped.
 * @param installationFlow use installation-specific team-member resolution
 */
export function collectInvoiceEmailRecipients(order: any, installationFlow: boolean): InvoiceRecipientBundle {
  const seen = new Set<string>();
  const licenseTypes: string[] = [];
  const emails: string[] = [];

  const push = (raw: string, lt: string) => {
    const e = String(raw ?? "").trim();
    if (!e) return;
    const key = e.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    licenseTypes.push(lt);
    emails.push(e);
  };

  const firm = resolveBrokerageFirmEmail(order);
  if (firm) push(firm, "BROKERAGE_FIRM");

  const team = installationFlow
    ? resolveInstallationBrokerageTeamMemberEmail(order) || resolveBrokerageTeamMemberEmail(order)
    : resolveBrokerageTeamMemberEmail(order);
  if (team) push(team, "BROKERAGE_TEAM_MEMBER");

  /** Installation jobs often store the agent/contact on `user_email` only (see quote send flow). */
  const customer = installationFlow
    ? firstNonEmpty(
        order?.customer_email,
        order?.user_email,
        order?.agent_email,
        order?.agent?.email,
        order?.customer?.email
      )
    : firstNonEmpty(order?.customer_email, order?.user_email);
  if (customer) push(customer, "AGENT");

  return { licenseTypes, emails };
}

function extractEmailsFromAssignedUsersPayload(payload: unknown): string[] {
  const root = payload as Record<string, unknown> | null | undefined;
  const data = (root?.data as Record<string, unknown> | undefined) ?? root;
  const raw =
    (Array.isArray(data?.account_emails) ? data.account_emails : null) ??
    (Array.isArray(root?.account_emails) ? root.account_emails : null) ??
    (Array.isArray(data) ? data : null) ??
    (Array.isArray(root) ? root : null) ??
    [];

  const seen = new Set<string>();
  const emails: string[] = [];
  for (const item of raw as unknown[]) {
    const email =
      typeof item === "string"
        ? item.trim()
        : String(
            (item as Record<string, unknown> | null | undefined)?.email ??
              (item as Record<string, unknown> | null | undefined)?.Email ??
              ""
          ).trim();
    if (!email) continue;
    const key = email.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    emails.push(email);
  }
  return emails;
}

/** `GET /roles/{roleName}/assigned-users` — emails for invoice CC-style recipients. */
export async function fetchRoleAssignedUserEmails(
  token: string,
  roleName: string = INVOICE_ACCOUNTS_MANAGEMENT_ROLE
): Promise<string[]> {
  const trimmedToken = String(token || "").trim();
  if (!trimmedToken) return [];

  try {
    const response = await fetch(
      `${process.env.REACT_APP_API_URL}/roles/${encodeURIComponent(roleName)}/assigned-users`,
      {
        method: "GET",
        headers: { Authorization: `Bearer ${trimmedToken}` },
      }
    );
    if (!response.ok) {
      const errText = await response.text().catch(() => "");
      safeConsole.warn(
        `[invoiceEmail] assigned-users fetch failed (${response.status}) for role "${roleName}"`,
        errText
      );
      return [];
    }
    const json = await response.json();
    return extractEmailsFromAssignedUsersPayload(json);
  } catch (e) {
    safeConsole.warn("[invoiceEmail] assigned-users fetch error:", e);
    return [];
  }
}

export function mergeInvoiceRecipientEmails(
  bundle: InvoiceRecipientBundle,
  extraEmails: string[],
  extraLicenseType = "SIGNAGE_FIRM"
): InvoiceRecipientBundle {
  if (!extraEmails.length) return bundle;

  const seen = new Set(bundle.emails.map((e) => e.toLowerCase()));
  const licenseTypes = [...bundle.licenseTypes];
  const emails = [...bundle.emails];

  for (const raw of extraEmails) {
    const e = String(raw ?? "").trim();
    if (!e) continue;
    const key = e.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    licenseTypes.push(extraLicenseType);
    emails.push(e);
  }

  return { licenseTypes, emails };
}

/** Customer/brokerage recipients plus Accounts & Management emails (kept separate for send). */
export async function buildInvoiceSendRecipientPlan(
  order: any,
  installationFlow: boolean,
  token: string
): Promise<InvoiceSendRecipientPlan> {
  const customerRecipients = collectInvoiceEmailRecipients(order, installationFlow);
  const accountEmailsRaw = await fetchRoleAssignedUserEmails(token);
  const customerSet = new Set(customerRecipients.emails.map((e) => e.toLowerCase()));
  const accountEmails = accountEmailsRaw.filter((e) => !customerSet.has(e.toLowerCase()));
  return { customerRecipients, accountEmails };
}

/** @deprecated Use {@link buildInvoiceSendRecipientPlan} — account emails are sent in a separate trigger call. */
export async function collectInvoiceEmailRecipientsForSend(
  order: any,
  installationFlow: boolean,
  token: string
): Promise<InvoiceRecipientBundle> {
  const plan = await buildInvoiceSendRecipientPlan(order, installationFlow, token);
  return mergeInvoiceRecipientEmails(plan.customerRecipients, plan.accountEmails);
}

/** Send invoice trigger to customer/brokerage recipients and Accounts & Management assignees. */
export async function sendInvoiceTriggerEmails(
  baseParams: Record<string, string>,
  plan: InvoiceSendRecipientPlan,
  from: string,
  token: string,
  pdfAttachment: Record<string, string>
): Promise<void> {
  const { sendTriggerEmails } = await import("./emailService");

  if (plan.customerRecipients.emails.length > 0) {
    await sendTriggerEmails(
      INVOICE_EMAIL_TRIGGER,
      plan.customerRecipients.licenseTypes,
      baseParams,
      plan.customerRecipients.emails,
      from,
      token,
      pdfAttachment
    );
  }

  if (plan.accountEmails.length > 0) {
    await sendTriggerEmails(
      INVOICE_EMAIL_TRIGGER,
      "SIGNAGE_FIRM",
      baseParams,
      plan.accountEmails,
      from,
      token,
      pdfAttachment
    );
  }
}

function firstNonEmptyString(...vals: unknown[]): string {
  for (const v of vals) {
    const s = String(v ?? "").trim();
    if (s) return s;
  }
  return "";
}

function formatUsdAmount(value: unknown): string {
  const n = Number(value ?? 0);
  const safe = Number.isFinite(n) ? n : 0;
  return `US$${safe.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/** Params for {@link BATCH_INVOICE_EMAIL_TRIGGER}. */
export function buildBatchInvoiceEmailParams(report: any, reportMongoId = ""): Record<string, string> {
  const invoiceNumber =
    firstNonEmptyString(report?.report_id, report?.batch_invoice_id, report?._id, report?.id) || "—";
  const companyName = firstNonEmptyString(report?.bill_from?.name, report?.bill_from_contact?.name) || "—";
  const billToName = firstNonEmptyString(report?.bill_to?.name);
  const customerName = billToName.split(",")[0].trim() || "there";
  const invoiceTotal = formatUsdAmount(
    report?.report_original_balance ?? report?.totals?.grand_total ?? report?.kpis?.original
  );
  const paidAmount = formatUsdAmount(
    report?.report_paid_amount ?? report?.paid_amount ?? report?.totals?.paid
  );
  const remainingBalance = formatUsdAmount(
    report?.report_remaining_balance ?? report?.totals?.outstanding_balance ?? report?.balance_amount
  );
  const sellerUserId = firstNonEmptyString(
    report?.owner_node_id,
    report?.user_id,
    report?.bill_from?.id,
    report?.bill_from?._id
  );
  const mongoId = firstNonEmptyString(reportMongoId, report?._id, report?.id);

  return {
    customer_name: customerName,
    invoice_number: invoiceNumber,
    invoice_date: formatDDMMYYYY(parseOrderDateValue(report?.report_date)),
    invoice_total: invoiceTotal,
    paid_amount: paidAmount,
    remaining_balance: remainingBalance,
    company_name: companyName,
    seller_org_id: sellerUserId,
    vendor_org_id: sellerUserId,
    reportMongoId: mongoId,
  };
}

function remainingBatchBalance(report: any): number {
  const candidates = [
    report?.report_remaining_balance,
    report?.totals?.outstanding_balance,
    report?.balance_amount,
    report?.kpis?.balance,
  ];
  for (const value of candidates) {
    const n = Number(value ?? 0);
    if (Number.isFinite(n) && n > 0) return n;
  }
  return 0;
}

function batchInvoiceSupplierUserId(report: any, fallbackUserId = ""): string {
  return firstNonEmptyString(
    report?.owner_node_id,
    fallbackUserId,
    report?.user_id,
    report?.bill_from?.id,
    report?.bill_from?._id
  );
}

/** Public batch report URL with Stripe query params — same pattern as order-summary `viewPayUrl`. */
export async function buildBatchInvoicePublicViewPayUrl(
  report: any,
  token: string,
  reportMongoId: string,
  supplierUserId = ""
): Promise<string> {
  const baseUrl = (
    process.env.REACT_APP_BASE_URL ||
    (typeof window !== "undefined" ? window.location.origin : "") ||
    ""
  ).replace(/\/+$/, "");
  const id = firstNonEmptyString(reportMongoId, report?._id, report?.id);
  const payUserId = batchInvoiceSupplierUserId(report, supplierUserId);

  const qs: string[] = [];
  if (payUserId) qs.push(`supplier_id=${encodeURIComponent(payUserId)}`);

  // Mute Stripe payment-intent + gateway lookup on Send Invoice OK.
  // try {
  //   const { createStripePaymentIntent, fetchPaymentGatewaysCreatedByUserId } = await import(
  //     "components/proejctsandquoteapicalls"
  //   );
  //   const remainingToCharge = remainingBatchBalance(report);
  //   const amountCents = Math.max(0, Math.round(remainingToCharge * 100));
  //   let stripeClientSecret = "";
  //   if (token && payUserId && amountCents > 0) {
  //     const pi = await createStripePaymentIntent(token, payUserId, {
  //       amount: amountCents,
  //       currency: "usd",
  //     });
  //     stripeClientSecret = String((pi as any)?.client_secret || (pi as any)?.clientSecret || "").trim();
  //   }
  //   let stripePublishableKeyForUrl = "";
  //   if (token && payUserId) {
  //     const gateways = await fetchPaymentGatewaysCreatedByUserId(token, payUserId);
  //     const stripeGw = (Array.isArray(gateways) ? gateways : []).find(
  //       (g: any) => String(g?.gateway || "").toLowerCase() === "stripe"
  //     );
  //     stripePublishableKeyForUrl = String(stripeGw?.publishable_key || "").trim();
  //   }
  //   if (stripeClientSecret) qs.push(`client_secret=${encodeURIComponent(stripeClientSecret)}`);
  //   if (stripePublishableKeyForUrl) qs.push(`publishable_key=${encodeURIComponent(stripePublishableKeyForUrl)}`);
  // } catch (e) {
  //   safeConsole.warn("Stripe helpers (batch invoice email):", e);
  // }

  const viewPayUrl = `${baseUrl}/public/batchPaymentReport/${encodeURIComponent(id)}`;
  return qs.length ? `${viewPayUrl}?${qs.join("&")}` : viewPayUrl;
}

/** One View & Pay URL for every license. The page reads the login cookie and opens the matching sidebar. */
export function batchInvoiceViewPayLoginUrl(reportMongoId: string, _licenseType?: string): string {
  const baseUrl = (
    process.env.REACT_APP_BASE_URL ||
    (typeof window !== "undefined" ? window.location.origin : "") ||
    ""
  ).replace(/\/+$/, "");
  const id = String(reportMongoId || "").trim();
  return `${baseUrl}/users/batchPaymentReport/${encodeURIComponent(id)}`;
}

export async function sendBatchInvoiceTriggerEmails(
  baseParams: Record<string, string>,
  emails: string[],
  licenseTypes: string[],
  from: string,
  token: string
  // pdfAttachment: Record<string, string>
): Promise<void> {
  const { sendTriggerEmails } = await import("./emailService");
  const recs = (emails || []).map((e) => String(e || "").trim()).filter((e) => e.includes("@"));
  if (recs.length === 0) return;
  const types =
    licenseTypes.length === recs.length
      ? licenseTypes
      : recs.map((_, i) => licenseTypes[i] || "BROKERAGE_FIRM");
  const reportMongoId = String(baseParams.reportMongoId || "").trim();
  await Promise.all(
    recs.map((email, index) => {
      const licenseType = types[index] || "BROKERAGE_FIRM";
      return sendTriggerEmails(
        BATCH_INVOICE_EMAIL_TRIGGER,
        licenseType,
        {
          ...baseParams,
          viewPayUrl: batchInvoiceViewPayLoginUrl(reportMongoId, licenseType),
        },
        [email],
        from,
        token
      );
    })
  );
}
